import './loanDocumentSuccessPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import uniqueId from 'lodash/uniqueId';
import { ReactNode, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import heroSuccess from '../../../assets/success-hero.svg';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { resetStore } from '../../../store/_core/resetStore';
import useLoanDocumentStore from '../../../store/loanDocumentStore/useLoanDocumentStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { handleSubmit } = useForm();

  const {
    computed: { userFirstName },
  } = useUserStore();

  useEffect((): (() => void) => {
    // Reset store when unmounting
    return (): void => {
      resetStore(useLoanDocumentStore);
    };
  }, []);

  const formSubmit = (): void => {
    // replace, as we do not want the user to come back to this page
    navigate('/dashboard/garage', { replace: true });
  };

  return (
    <div className="loan-document-success-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header showDrawerToggle showLogo />

          <hr />

          <main>
            <figure>
              <img alt={t('imageAlts.heroSuccess')} src={heroSuccess} />
            </figure>
            <form
              data-testid={locator.pages.loanDocumentSuccess.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.loanDocumentSuccess.form.h1}>
                  <Trans
                    components={[<br key={uniqueId('br-')} />]}
                    i18nKey={'LoanDocumentSuccessPage.title'}
                    values={{
                      firstName: userFirstName,
                    }}
                  />
                </h1>
                <h2 data-testid={locator.pages.loanDocumentSuccess.form.h2}>
                  {t('LoanDocumentSuccessPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <Button
                    data-testid={
                      locator.pages.loanDocumentSuccess.form.submitButton
                    }
                    fullWidth
                    submit
                  >
                    {t('LoanDocumentSuccessPage.submit')}
                  </Button>
                </div>
              </div>
              <footer
                data-testid={locator.pages.loanDocumentSuccess.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'LoanDocumentSuccessPage';
